// client/src/pages/seo/RestaurantDigitalisierungDe.jsx
import React, { useEffect, useMemo, useState } from "react";
import { Link } from "react-router-dom";
import SeoRelatedLinks from "../../components/seo/SeoRelatedLinks.jsx";
import SeoHeader from "../../components/seo/SeoHeader.jsx";
import SeoContactSection from "../../components/seo/SeoContactSection.jsx";
import SeoWorkSteps from "../../components/seo/SeoWorkSteps.jsx";

const sections = [
  {
    id: "check",
    label: "Wie digital ist Ihr Restaurant bereits?",
  },
  {
    id: "definition",
    label: "Was bedeutet Restaurant-Digitalisierung?",
  },
  {
    id: "why",
    label: "Warum Restaurant-Digitalisierung heute wichtig ist",
  },
  {
    id: "steps",
    label: "Restaurant-Digitalisierung Schritt für Schritt starten",
  },
  {
    id: "channels",
    label: "Die wichtigsten digitalen Bausteine für Restaurants",
  },
  {
    id: "guest",
    label: "Digitalisierung aus Sicht der Gäste denken",
  },
  {
    id: "team",
    label: "Digitalisierung muss auch dem Team helfen",
  },
  {
    id: "platforms",
    label: "Warum Restaurants digitale Unabhängigkeit brauchen",
  },
  {
    id: "mistakes",
    label: "Häufige Fehler bei der Restaurant-Digitalisierung",
  },
  {
    id: "bestplace",
    label: "Wie Best Place bei der Restaurant-Digitalisierung helfen kann",
  },
  {
    id: "faq",
    label: "FAQ zur Restaurant-Digitalisierung",
  },
];

const checklist = [
  "Ihre Speisekarte ist online schwer zu finden oder nicht aktuell.",
  "Sie möchten QR-Code, Speisekarte, Reservierung und Website besser verbinden.",
  "Reservierungen kommen über mehrere Kanäle unstrukturiert an.",
  "Sie aktualisieren regelmäßig Preise, Fotos oder Verfügbarkeit.",
  "Sie möchten weniger von Papier, PDFs oder manuellen Prozessen abhängig sein.",
  "Gäste erwarten digitale Informationen bereits vor dem Besuch.",
  "Sie möchten Abläufe vereinfachen, ohne den persönlichen Service zu ersetzen.",
  "Sie möchten Schritt für Schritt digitaler werden, ohne sofort ein großes System einzuführen.",
  "Sie möchten weniger nur von Plattformen und sozialen Medien abhängig sein.",
];

const CALLBACK_API_ORIGIN =
  typeof window !== "undefined" &&
  (window.location.hostname === "localhost" ||
    window.location.hostname === "127.0.0.1")
    ? "http://localhost:3030"
    : "";

function getResult(score) {
  if (score <= 3) {
    return {
      title: "Digitalisierung ist vielleicht noch nicht dringend",
      text:
        "Wenn Ihre wichtigsten Informationen online bereits aktuell sind und die täglichen Abläufe gut funktionieren, können Sie klein starten: mit Speisekarte, QR-Code oder einer einfachen Website.",
      tone: "low",
    };
  }

  if (score <= 6) {
    return {
      title: "Schrittweise Digitalisierung ist sinnvoll",
      text:
        "Ihr Restaurant kann davon profitieren, einzelne digitale Bausteine besser zu verbinden: Speisekarte, QR-Code, Website, Reservierung und Menüverwaltung.",
      tone: "medium",
    };
  }

  return {
    title: "Digitalisierung kann ein zentraler Hebel werden",
    text:
      "Mehrere Punkte sprechen dafür, dass Ihr Restaurant eine klare digitale Strategie braucht: bessere Online-Sichtbarkeit, eine aktuellere Speisekarte, weniger manuelle Arbeit und einfachere Wege für Gäste.",
    tone: "high",
  };
}

function VisualIdea({ children }) {
  return (
    <div className="seoVisualIdea" aria-label="Idee für Illustration">
      <div className="seoVisualIdea__icon" aria-hidden="true">
        ✦
      </div>
      <div>
        <strong>Idee für Illustration:</strong>
        <p>{children}</p>
      </div>
    </div>
  );
}

export default function RestaurantDigitalisierungDe() {
  const [checked, setChecked] = useState([]);

  const score = checked.length;
  const result = useMemo(() => getResult(score), [score]);

  useEffect(() => {
    document.documentElement.setAttribute("lang", "de");
    document.title =
      "Restaurant-Digitalisierung: Schritt für Schritt digital werden | Best Place";
  }, []);

  const toggle = (item) => {
    setChecked((prev) =>
      prev.includes(item) ? prev.filter((x) => x !== item) : [...prev, item]
    );
  };

  const scrollToSection = (id) => {
    document.getElementById(id)?.scrollIntoView({
      behavior: "smooth",
      block: "start",
    });
  };

  return (
    <main className="qrArticle">
      <SeoHeader
        lang="de"
        langLinks={{
          en: "/en/restaurant-digitalization",
          de: "/de/restaurant-digitalisierung",
          no: "/no/restaurant-digitalisering",
        }}
      />

      <section className="qrArticle__hero">
        <div className="qrArticle__heroInner">
          <h1 className="qrArticle__heroTitle">
            <span>
              Restaurant-Digitalisierung: Wie Restaurants Schritt für Schritt
              digital werden
            </span>
          </h1>

          <div className="qrArticle__topsection">
            <div className="qrArticle__topsection--img-wrapper">
              <div className="qrArticle__topsection--img"></div>
            </div>

            <div className="qrArticle__topsection--text-wrapper">
              <p className="seoHero__lead">
                Restaurant-Digitalisierung bedeutet nicht, persönlichen Service
                zu ersetzen. Es bedeutet, die richtigen digitalen Bausteine dort
                einzusetzen, wo sie Gästen und Team wirklich helfen.
              </p>

              <p className="seoHero__text">
                Für viele Restaurants beginnt Digitalisierung nicht mit einem
                großen System. Sie beginnt mit einfachen Fragen: Ist unsere
                Speisekarte online aktuell? Können Gäste digital reservieren?
                Funktioniert der QR-Code? Kann das Team Preise, Fotos und
                Verfügbarkeit schnell ändern?
              </p>
            </div>
          </div>
        </div>
      </section>

      <SeoContactSection lang="de" apiOrigin={CALLBACK_API_ORIGIN} />

      <section className="articls">
        <h2>Inhalt des Artikels</h2>

        <nav className="qrArticle__nav" aria-label="Artikelnavigation">
          <ol className="qrArticle__navList">
            {sections.map((item, index) => (
              <li key={item.id} className="qrArticle__navItem">
                <button
                  type="button"
                  className="qrArticle__navButton"
                  onClick={() => scrollToSection(item.id)}
                >
                  <span className="qrArticle__navNumber">
                    {String(index + 1).padStart(2, "0")}
                  </span>

                  <span className="qrArticle__navLabel">{item.label}</span>
                </button>
              </li>
            ))}
          </ol>
        </nav>
      </section>

      <article>
        <section id="check" className="qrArticle__quizWave">
          <div className="qrArticle__quizInner">
            <h2>Wie digital ist Ihr Restaurant bereits?</h2>

            <p>
              Wählen Sie die Aussagen aus, die zu Ihrem Restaurant passen. Das
              Ergebnis ist bewusst ehrlich: Nicht jedes Restaurant braucht sofort
              ein großes digitales System. Viele Restaurants profitieren aber
              davon, Speisekarte, QR-Code, Website und Reservierung Schritt für
              Schritt besser zu verbinden.
            </p>

            <div className="qrArticle__checkGrid">
              {checklist.map((item) => (
                <label key={item} className="qrArticle__checkItem">
                  <input
                    type="checkbox"
                    checked={checked.includes(item)}
                    onChange={() => toggle(item)}
                  />
                  <span className="text">{item}</span>
                </label>
              ))}
            </div>

            <div className={`qrArticle__result qrArticle__result--${result.tone}`}>
              <div className="qrArticle__score">
                {score} / {checklist.length}
              </div>

              <div>
                <strong>{result.title}</strong>
                <p>{result.text}</p>
              </div>
            </div>
          </div>
        </section>

        <section id="definition" className="acquaintance">
          <h2>Was bedeutet Restaurant-Digitalisierung?</h2>

          <p>
            Restaurant-Digitalisierung bedeutet, analoge oder manuelle Prozesse
            dort mit digitalen Werkzeugen zu unterstützen, wo es sinnvoll ist:
            Speisekarte, QR-Code, Reservierung, Bestellung, Zahlung, Website,
            Bewertungen, Verwaltung und interne Abläufe.
          </p>

          <p>
            Es geht nicht darum, jede Interaktion mit Gästen zu automatisieren.
            Gute Gastfreundschaft bleibt persönlich. Digitalisierung soll
            unnötige Reibung reduzieren: weniger veraltete Informationen,
            weniger verlorene Anfragen und weniger doppelte manuelle Arbeit.
          </p>

          <ul className="qrArticle__block-li">
            <li className="text__seo-li">Digitale Speisekarte.</li>
            <li className="text__seo-li">QR-Code-Zugang.</li>
            <li className="text__seo-li">Restaurant-Website.</li>
            <li className="text__seo-li">Online-Tischreservierung.</li>
            <li className="text__seo-li">Online-Menüverwaltung.</li>
            <li className="text__seo-li">Digitale Bestellabläufe.</li>
            <li className="text__seo-li">Kontaktlose Zahlung.</li>
            <li className="text__seo-li">Strukturierte Verwaltung.</li>
          </ul>

          <h3>Digitale Werkzeuge sollten Reibung reduzieren</h3>

          <p>
            Die besten digitalen Werkzeuge machen ein Restaurant nicht kälter
            oder komplizierter. Sie machen Informationen leichter auffindbar,
            Änderungen einfacher verwaltbar und den Weg der Gäste verständlicher.
          </p>
        </section>

        <section id="why" className="qrArticle__quizWave">
          <div className="qrArticle__quizInner">
            <h2>Warum Restaurant-Digitalisierung heute wichtig ist</h2>

            <p>
              Gäste nutzen digitale Wege längst im Alltag: Sie suchen
              Restaurants online, prüfen Speisekarten, reservieren Tische und
              bevorzugen zunehmend kontaktlose Zahlungen.
            </p>

            <p>
              Ein Restaurant muss nicht alles digitalisieren. Es sollte aber die
              wichtigsten Kontaktpunkte gut abdecken: Online-Sichtbarkeit,
              aktuelle Speisekarteninformationen, QR-Code-Zugang, Reservierung
              und mobile Nutzbarkeit.
            </p>

            <h3>Dort starten, wo Gäste bereits digital handeln</h3>

            <p>
              Die praktische Schlussfolgerung ist einfach: Digitalisierung
              sollte dort beginnen, wo Gäste digitale Werkzeuge bereits nutzen —
              Suche, Speisekarte, Reservierung und Zahlung.
            </p>
          </div>
        </section>

        <section id="steps" className="acquaintance">
          <h2>Restaurant-Digitalisierung Schritt für Schritt starten</h2>

          <p>
            Der häufigste Fehler ist, Digitalisierung als ein großes
            Alles-oder-nichts-Projekt zu behandeln. Für viele Restaurants ist
            ein schrittweiser Ansatz besser.
          </p>

          <ol className="seoSteps">
            <li className="text__seo-li">
              Die wichtigsten Informationen online klar darstellen.
            </li>
            <li className="text__seo-li">Eine digitale Speisekarte erstellen.</li>
            <li className="text__seo-li">
              QR-Code-Zugang für Gäste nutzen.
            </li>
            <li className="text__seo-li">
              Die Speisekarte online bearbeitbar machen.
            </li>
            <li className="text__seo-li">
              Reservierung als nächsten Schritt hinzufügen.
            </li>
            <li className="text__seo-li">
              Später Bestellung oder Zahlung ergänzen, wenn es sinnvoll ist.
            </li>
            <li className="text__seo-li">
              Regelmäßig prüfen, was Gäste tatsächlich nutzen.
            </li>
          </ol>

          <h3>Digitalisierung ist ein System sinnvoller Verbesserungen</h3>

          <p>
            Es ist nicht ein Knopf und nicht ein riesiger Starttermin. Es ist
            eine Reihe kleiner Verbesserungen, die ein Restaurant leichter
            auffindbar, leichter verständlich und leichter verwaltbar machen.
          </p>

          <VisualIdea>
            Eine schrittweise Roadmap zur Digitalisierung: Website, digitale
            Speisekarte, QR-Code, Online-Reservierung, Menüverwaltung und später
            Bestellung oder Zahlung.
          </VisualIdea>
        </section>

        <section id="channels" className="qrArticle__quizWave">
          <div className="qrArticle__quizInner">
            <h2>Die wichtigsten digitalen Bausteine für Restaurants</h2>

            <p>
              Ein Restaurant braucht nicht sofort jede Technologie. Entscheidend
              ist, die Bausteine auszuwählen, die zum eigenen Geschäftsmodell
              passen.
            </p>

            <div className="seoComparison">
              <div className="seoComparison__col">
                <h3>Digitale Grundlage</h3>

                <ul className="qrArticle__block-li">
                  <li className="text__seo-li">
                    <strong>Digitale Speisekarte:</strong> Speisekarte online
                    anzeigen und Preise sowie Fotos aktuell halten.
                  </li>
                  <li className="text__seo-li">
                    <strong>QR-Code:</strong> Gästen schnellen Zugang am Tisch
                    oder im Fenster geben.
                  </li>
                  <li className="text__seo-li">
                    <strong>Website:</strong> Speisekarte, Kontakt,
                    Öffnungszeiten und Reservierung zusammenführen.
                  </li>
                </ul>
              </div>

              <div className="seoComparison__col seoComparison__col--highlight">
                <h3>Nächste digitale Schritte</h3>

                <ul className="qrArticle__block-li">
                  <li className="text__seo-li">
                    <strong>Online-Reservierung:</strong> Reservierungsanfragen
                    auch außerhalb der Öffnungszeiten annehmen.
                  </li>
                  <li className="text__seo-li">
                    <strong>Bestellsystem:</strong> Abholung, Bestellung am
                    Tisch oder Anfragen strukturierter machen.
                  </li>
                  <li className="text__seo-li">
                    <strong>Digitale Zahlung:</strong> Zahlungen schneller und
                    bequemer machen.
                  </li>
                </ul>
              </div>
            </div>

            <div className="seoCompareSmall">
              <div>
                <strong>Verwandte Seite</strong>
                <p>
                  <Link to="/de/digitale-speisekarte">
                    Digitale Speisekarte für Restaurants
                  </Link>
                </p>
              </div>

              <div>
                <strong>Verwandte Seite</strong>
                <p>
                  <Link to="/de/online-tischreservierung">
                    Online-Tischreservierung
                  </Link>
                </p>
              </div>
            </div>
          </div>
        </section>

        <section id="guest" className="acquaintance">
          <h2>Digitalisierung aus Sicht der Gäste denken</h2>

          <p>
            Gäste denken nicht in Software-Modulen. Sie haben einfache Fragen:
            Wo finde ich das Restaurant? Was kann ich dort essen? Was kostet es?
            Kann ich reservieren? Kann ich bezahlen? Sind die Informationen
            aktuell?
          </p>

          <ol className="seoSteps">
            <li className="text__seo-li">Restaurant finden.</li>
            <li className="text__seo-li">Speisekarte ansehen.</li>
            <li className="text__seo-li">QR-Code nutzen.</li>
            <li className="text__seo-li">Tisch reservieren.</li>
            <li className="text__seo-li">Besuch planen.</li>
          </ol>

          <p>
            Je kürzer und klarer dieser Weg ist, desto geringer ist die Gefahr,
            dass Gäste unterwegs die Orientierung verlieren.
          </p>
        </section>

        <section id="team" className="qrArticle__quizWave">
          <div className="qrArticle__quizInner">
            <h2>Digitalisierung muss auch dem Team helfen</h2>

            <p>
              Ein digitales System ist nur dann gut, wenn es nicht nur für Gäste
              gut aussieht, sondern auch im täglichen Ablauf des Teams
              funktioniert.
            </p>

            <div className="seoComparison">
              <div className="seoComparison__col">
                <h3>Schlechte Digitalisierung</h3>

                <ul className="qrArticle__block-li">
                  <li className="text__seo-li">
                    Zu viele getrennte Werkzeuge.
                  </li>
                  <li className="text__seo-li">Komplizierte Nutzung.</li>
                  <li className="text__seo-li">
                    Unklare Verantwortlichkeiten.
                  </li>
                  <li className="text__seo-li">
                    Mehr Arbeit statt weniger Arbeit.
                  </li>
                </ul>
              </div>

              <div className="seoComparison__col seoComparison__col--highlight">
                <h3>Gute Digitalisierung</h3>

                <ul className="qrArticle__block-li">
                  <li className="text__seo-li">Klare Abläufe.</li>
                  <li className="text__seo-li">Einfache Verwaltung.</li>
                  <li className="text__seo-li">Aktuelle Informationen.</li>
                  <li className="text__seo-li">Weniger doppelte Arbeit.</li>
                  <li className="text__seo-li">Mehr Zeit für Gäste.</li>
                </ul>
              </div>
            </div>

            <p>
              Eine digitale Speisekarte, die niemand aktualisiert, ist nicht
              hilfreich. Eine Reservierungsfunktion, die das Team zu spät sieht,
              schafft neue Probleme. Digitalisierung muss zum echten Arbeitsablauf
              passen.
            </p>
          </div>
        </section>

        <section id="platforms" className="acquaintance">
          <h2>Warum Restaurants digitale Unabhängigkeit brauchen</h2>

          <p>
            Plattformen, soziale Medien und Suchmaschinen sind wichtig. Sie
            können Reichweite bringen. Trotzdem sollte ein Restaurant nicht
            vollständig von externen Kanälen abhängig sein.
          </p>

          <p>
            Die eigene digitale Grundlage eines Restaurants — Website,
            Speisekarte, QR-Code, Reservierung — gibt mehr Kontrolle über
            Inhalte, Darstellung und den Weg der Gäste.
          </p>

          <div className="seoComparison">
            <div className="seoComparison__col">
              <h3>Nur externe Kanäle</h3>

              <ul className="qrArticle__block-li">
                <li className="text__seo-li">
                  Weniger Kontrolle über die Darstellung.
                </li>
                <li className="text__seo-li">
                  Informationen sind über Plattformen verteilt.
                </li>
                <li className="text__seo-li">
                  Layout und Regeln können sich ändern.
                </li>
                <li className="text__seo-li">
                  Speisekarte, QR-Code und Reservierung bleiben oft getrennt.
                </li>
              </ul>
            </div>

            <div className="seoComparison__col seoComparison__col--highlight">
              <h3>Eigene digitale Grundlage</h3>

              <ul className="qrArticle__block-li">
                <li className="text__seo-li">Zentrale Restaurantseite.</li>
                <li className="text__seo-li">Eigene digitale Speisekarte.</li>
                <li className="text__seo-li">Zielseite für den QR-Code.</li>
                <li className="text__seo-li">Reservierungsweg.</li>
                <li className="text__seo-li">
                  Mehr Kontrolle über die Gästeerfahrung.
                </li>
              </ul>
            </div>
          </div>
        </section>

        <section id="mistakes" className="qrArticle__quizWave">
          <div className="qrArticle__quizInner">
            <h2>Häufige Fehler bei der Restaurant-Digitalisierung</h2>

            <p>
              Digitalisierung sollte die tägliche Arbeit klarer machen, nicht
              schwerer. Viele Probleme entstehen, wenn ein Restaurant mit zu
              vielen Tools oder ohne klares Ziel startet.
            </p>

            <div className="seoComparison">
              <div className="seoComparison__col">
                <h3>Häufige Fehler</h3>

                <ul className="qrArticle__block-li">
                  <li className="text__seo-li">
                    Zu viele Tools werden auf einmal eingeführt.
                  </li>
                  <li className="text__seo-li">
                    Die digitale Veränderung hat kein klares Ziel.
                  </li>
                  <li className="text__seo-li">
                    Der Weg der Gäste wird nicht auf dem Smartphone getestet.
                  </li>
                  <li className="text__seo-li">
                    Das Team wird nicht in den Ablauf einbezogen.
                  </li>
                  <li className="text__seo-li">
                    Alte Preise, Öffnungszeiten oder Fotos bleiben online.
                  </li>
                  <li className="text__seo-li">
                    Das Restaurant hängt nur von externen Plattformen ab.
                  </li>
                </ul>
              </div>

              <div className="seoComparison__col seoComparison__col--highlight">
                <h3>Besser</h3>

                <ul className="qrArticle__block-li">
                  <li className="text__seo-li">
                    Mit einem sinnvollen digitalen Schritt starten.
                  </li>
                  <li className="text__seo-li">
                    Festlegen, welches Problem das Tool löst.
                  </li>
                  <li className="text__seo-li">
                    Den Ablauf auf echten Smartphones testen.
                  </li>
                  <li className="text__seo-li">
                    Das Team frühzeitig einbeziehen.
                  </li>
                  <li className="text__seo-li">
                    Speisekarte und Restaurantinformationen aktuell halten.
                  </li>
                  <li className="text__seo-li">
                    Eine eigene digitale Grundlage aufbauen.
                  </li>
                </ul>
              </div>
            </div>
          </div>
        </section>

        <div id="bestplace" className="qrArticle__anchorTarget">
          <SeoWorkSteps lang="de" />
        </div>

        <section id="faq" className="acquaintance">
          <h2>Häufige Fragen zur Restaurant-Digitalisierung</h2>

          <div className="seoFaq">
            <details>
              <summary>
                Muss ein Restaurant sofort alles digitalisieren?
              </summary>
              <p>
                Nein. Ein schrittweiser Ansatz ist oft besser: digitale
                Speisekarte, QR-Code, Website, Reservierung und danach weitere
                Funktionen.
              </p>
            </details>

            <details>
              <summary>Ersetzt Digitalisierung den persönlichen Service?</summary>
              <p>
                Nein. Gute Digitalisierung unterstützt den Service, indem sie
                Informationen, Reservierungen und Menüverwaltung einfacher macht.
              </p>
            </details>

            <details>
              <summary>Was sollte zuerst digitalisiert werden?</summary>
              <p>
                Die Speisekarte ist oft der beste Startpunkt, weil Gäste sie vor
                dem Besuch und vor der Bestellung sehen möchten.
              </p>
            </details>

            <details>
              <summary>
                Ist Digitalisierung auch für kleine Restaurants sinnvoll?
              </summary>
              <p>
                Ja, besonders wenn kleine Betriebe Änderungen selbst verwalten
                und weniger von Druck, PDFs oder externer Hilfe abhängig sein
                möchten.
              </p>
            </details>

            <details>
              <summary>Welche Rolle spielt der QR-Code?</summary>
              <p>
                Der QR-Code kann der schnelle Einstiegspunkt sein: Gäste scannen
                den Code und öffnen Speisekarte, Restaurantseite oder
                Reservierungsablauf.
              </p>
            </details>
          </div>
        </section>
        <SeoRelatedLinks
          lang="de"
          group="business"
          exclude="/de/restaurant-digitalisierung"
        />
      </article>
    </main>
  );
}